import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

interface IApiEnvelope<T> {
  data: T;
  message?: string;
  meta?: unknown;
}

const BASE = '/api/v1/contacil/regras';

export type RegraDC = 'D' | 'C' | null;
export type RegraOrigem = 'hardcoded' | 'personalizada';

export interface IContaCilRegra {
  id: string; // "42" (personalizada) ou "hc-0" (hardcoded)
  origem: RegraOrigem;
  empresa_id?: number | null;
  empresa_nome?: string | null;
  empresa_external_id?: string | null;
  banco?: string | null; // slug; null = todos os bancos
  kw: string[];
  categoria: string;
  dc?: RegraDC;
  poc_search?: string[] | null;
  poc_codigo?: string | null;
  poc_nome?: string | null;
  ordem: number;
  ativa: boolean;
  kw_signature: string;
  criada_por_ia?: boolean;
  criada_por_usuario_id?: number | null;
  dt_criacao?: string | null;
}

export interface IContaCilRegraList {
  personalizadas: IContaCilRegra[];
  hardcoded: IContaCilRegra[];
  total: number;
}

export interface IRegrasCategoriaStat {
  categoria: string;
  regras: number;
  txns: number;
  tem_poc: boolean;
}

export interface IRegrasBancoStat {
  banco: string;
  cobertura_percent: number;
  total: number;
}

export interface IRegrasStats {
  total: number;
  hardcoded: number;
  personalizadas: number;
  ativas: number;
  inativas: number;
  com_poc: number;
  percent_com_poc: number;
  cobertura_percent: number;
  gap_outros_txns: number;
  gap_outros_percent: number;
  por_categoria: IRegrasCategoriaStat[];
  por_banco: IRegrasBancoStat[];
}

export interface IBancoOption {
  slug: string;
  nome: string;
}

export interface IOutrosRankingItem {
  amostra: string;
  exemplo: string;
  dc: 'D' | 'C';
  qtd: number;
  soma: number;
  sugestao_kw?: string | null;
}

export interface IRegraPreviewExemplo {
  id: number;
  data: string;
  historico: string;
  valor: number;
  dc: 'D' | 'C';
  categoria_atual?: string | null;
}

export interface IRegraPreviewResult {
  total_casados: number;
  ja_categorizados_diferente: number;
  exemplos: IRegraPreviewExemplo[];
}

export interface IRegrasFiltro {
  empresaId?: number | null;
  banco?: string | null;
  categoria?: string | null;
  search?: string | null;
  incluirHardcoded?: boolean;
  apenasAtivas?: boolean;
}

export interface IRegraPayload {
  empresa_id?: number | null;
  banco?: string | null;
  kw: string[];
  categoria: string;
  dc?: RegraDC;
  poc_search?: string[] | null;
  poc_codigo?: string | null;
  ordem?: number | null;
}

export interface IRegraPreviewDraft {
  empresa_id?: number | null;
  banco?: string | null;
  kw: string[];
  dc?: RegraDC;
  limit_exemplos?: number;
}

// ── Queries ────────────────────────────────────────────────────────────────

/** Lista regras (142 hardcoded + personalizadas) com filtros. */
export const useContaCilRegras = (filtro: IRegrasFiltro) =>
  useQuery({
    queryKey: ['contacil', 'regras', 'list', filtro] as const,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<IContaCilRegraList> => {
      const params: Record<string, string | number | boolean> = {};
      if (filtro.empresaId != null) params.empresa_id = filtro.empresaId;
      if (filtro.banco) params.banco = filtro.banco;
      if (filtro.categoria) params.categoria = filtro.categoria;
      if (filtro.search) params.search = filtro.search;
      if (filtro.incluirHardcoded === false) params.incluir_hardcoded = false;
      if (filtro.apenasAtivas) params.apenas_ativas = true;
      const res = await api.get<IApiEnvelope<IContaCilRegraList>>(BASE, { params });
      return res.data.data;
    },
  });

/** KPIs de regras + cobertura/gap (lancamento-derivado quando ha empresa). */
export const useContaCilRegrasStats = (empresaId?: number | null, banco?: string | null) =>
  useQuery({
    queryKey: ['contacil', 'regras', 'stats', empresaId ?? null, banco ?? null] as const,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IRegrasStats> => {
      const params: Record<string, string | number> = {};
      if (empresaId != null) params.empresa_id = empresaId;
      if (banco) params.banco = banco;
      const res = await api.get<IApiEnvelope<IRegrasStats>>(`${BASE}/stats`, { params });
      return res.data.data;
    },
  });

/**
 * Bancos pro seletor. Sem opts = catalogo completo (criar regra). Com
 * { empresaId, emUso:true } = so os bancos que a empresa usa (filtro).
 */
export const useContaCilRegrasBancos = (opts?: { empresaId?: number | null; emUso?: boolean }) =>
  useQuery({
    queryKey: ['contacil', 'regras', 'bancos', opts?.empresaId ?? null, !!opts?.emUso] as const,
    staleTime: opts?.emUso ? 60 * 1000 : 60 * 60 * 1000,
    queryFn: async (): Promise<IBancoOption[]> => {
      const params: Record<string, string | number | boolean> = {};
      if (opts?.emUso && opts?.empresaId != null) {
        params.empresa_id = opts.empresaId;
        params.em_uso = true;
      }
      const res = await api.get<IApiEnvelope<IBancoOption[]>>(`${BASE}/bancos`, { params });
      return res.data.data;
    },
  });

/** Categorias conhecidas (creatable select). */
export const useContaCilRegrasCategorias = (empresaId?: number | null) =>
  useQuery({
    queryKey: ['contacil', 'regras', 'categorias', empresaId ?? null] as const,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<string[]> => {
      const params: Record<string, number> = {};
      if (empresaId != null) params.empresa_id = empresaId;
      const res = await api.get<IApiEnvelope<string[]>>(`${BASE}/categorias`, { params });
      return res.data.data;
    },
  });

/** Ranking de "Outros" por empresa (caca de gaps). */
export const useContaCilOutrosRanking = (params: {
  empresaId?: number | null;
  banco?: string | null;
  ano?: number | null;
  mes?: number | null;
  limit?: number;
}) =>
  useQuery({
    queryKey: ['contacil', 'regras', 'outros', params] as const,
    enabled: params.empresaId != null,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<IOutrosRankingItem[]> => {
      const qp: Record<string, string | number> = {
        empresa_id: params.empresaId as number,
        limit: params.limit ?? 30,
      };
      if (params.banco) qp.banco = params.banco;
      if (params.ano) qp.ano = params.ano;
      if (params.mes) qp.mes = params.mes;
      const res = await api.get<IApiEnvelope<IOutrosRankingItem[]>>(`${BASE}/outros-ranking`, {
        params: qp,
      });
      return res.data.data;
    },
  });

/** Preview ao vivo: quantos/quais lancamentos a regra casaria (sem gravar). */
export const useContaCilRegraPreview = (draft: IRegraPreviewDraft, enabled: boolean) =>
  useQuery({
    queryKey: ['contacil', 'regras', 'preview', draft] as const,
    enabled: enabled && draft.empresa_id != null && draft.kw.length > 0,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<IRegraPreviewResult> => {
      const res = await api.post<IApiEnvelope<IRegraPreviewResult>>(`${BASE}/preview`, {
        empresa_id: draft.empresa_id,
        banco: draft.banco ?? null,
        kw: draft.kw,
        dc: draft.dc ?? null,
        limit_exemplos: draft.limit_exemplos ?? 8,
      });
      return res.data.data;
    },
  });

// ── Mutations ────────────────────────────────────────────────────────────────

const useInvalidarRegras = () => {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['contacil', 'regras'] });
};

export const useCriarRegra = () => {
  const invalidar = useInvalidarRegras();
  return useMutation({
    mutationFn: async (payload: IRegraPayload): Promise<IContaCilRegra> => {
      const res = await api.post<IApiEnvelope<IContaCilRegra>>(BASE, payload);
      return res.data.data;
    },
    onSuccess: invalidar,
  });
};

export const useEditarRegra = () => {
  const invalidar = useInvalidarRegras();
  return useMutation({
    mutationFn: async (params: {
      id: number;
      payload: Partial<IRegraPayload>;
    }): Promise<IContaCilRegra> => {
      const res = await api.put<IApiEnvelope<IContaCilRegra>>(
        `${BASE}/${params.id}`,
        params.payload,
      );
      return res.data.data;
    },
    onSuccess: invalidar,
  });
};

export const useExcluirRegra = () => {
  const invalidar = useInvalidarRegras();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await api.delete(`${BASE}/${id}`);
    },
    onSuccess: invalidar,
  });
};

/** Ativa/desativa regra (hardcoded ou personalizada) por signature + escopo. */
export const useToggleRegra = () => {
  const invalidar = useInvalidarRegras();
  return useMutation({
    mutationFn: async (params: {
      kw_signature: string;
      empresa_id?: number | null;
      banco?: string | null;
      ativar: boolean;
    }): Promise<void> => {
      const path = params.ativar ? 'ativar' : 'desativar';
      await api.post(`${BASE}/${path}`, {
        kw_signature: params.kw_signature,
        empresa_id: params.empresa_id ?? null,
        banco: params.banco ?? null,
      });
    },
    onSuccess: invalidar,
  });
};

export const useReordenarRegras = () => {
  const invalidar = useInvalidarRegras();
  return useMutation({
    mutationFn: async (params: {
      empresa_id?: number | null;
      banco?: string | null;
      ordem: number[];
    }): Promise<IContaCilRegra[]> => {
      const res = await api.patch<IApiEnvelope<IContaCilRegra[]>>(`${BASE}/reordenar`, {
        empresa_id: params.empresa_id ?? null,
        banco: params.banco ?? null,
        ordem: params.ordem,
      });
      return res.data.data;
    },
    onSuccess: invalidar,
  });
};

/** Aplica a regra retroativamente aos lancamentos ja importados (auto-conciliar). */
export const useAplicarRegra = () => {
  const invalidar = useInvalidarRegras();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      id: number,
    ): Promise<{ atualizados: number; conciliados: number; truncado: boolean }> => {
      const res = await api.post<
        IApiEnvelope<{ atualizados: number; conciliados: number; truncado: boolean }>
      >(`${BASE}/aplicar/${id}`);
      return res.data.data;
    },
    onSuccess: () => {
      invalidar();
      // afeta lancamentos da Conciliar tambem
      qc.invalidateQueries({ queryKey: ['contacil', 'lancamentos'] });
    },
  });
};

// ── IA (Claude) ──────────────────────────────────────────────────────────────

export interface IIaResultado {
  modo: string;
  regras_criadas: number;
  conciliados: number;
  analisados: number;
  descartados_baixa_confianca: number;
  custo_usd: number;
  tokens_in: number;
  tokens_out: number;
  modelo?: string | null;
  execucao_id?: number | null;
  restantes_pendentes?: number;
}

export interface IIaConciliarPreview {
  empresa_id: number;
  pendentes: number;
  ja_conciliados_ia: number;
  chunk: number;
  modelo: string;
  custo_lote_min: number;
  custo_lote_max: number;
  pode_usar_ia: boolean;
  motivo_bloqueio?: string | null;
  tem_conta_vinculada?: boolean;
}

export interface IIaExecucao {
  id: number;
  modo: string;
  modelo: string;
  qtd_regras_criadas: number;
  qtd_lancamentos_conciliados: number;
  tokens_in: number;
  tokens_out: number;
  custo_usd?: number | null;
  revertida: boolean;
  dt_criacao?: string | null;
}

const useInvalidarIa = () => {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['contacil', 'regras'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'lancamentos'] });
  };
};

/** IA gera regras a partir dos gaps e aplica (auto). */
export const useContaCilIaGerar = () => {
  const invalidar = useInvalidarIa();
  return useMutation({
    mutationFn: async (empresaId: number): Promise<IIaResultado> => {
      const res = await api.post<IApiEnvelope<IIaResultado>>(`${BASE}/ia/gerar`, null, {
        params: { empresa_id: empresaId },
      });
      return res.data.data;
    },
    onSuccess: invalidar,
  });
};

/**
 * Recorte da tela Conciliar (ano/mês/banco/conta) aplicado às ações em massa.
 *
 * Existe porque o auto-conciliar ignorava os filtros: o contador escolhia um
 * mês, via a lista e os KPIs zerados, e a ação mexia na empresa inteira — fora
 * da janela que ele estava olhando, gastando crédito de IA e gravando
 * lançamento contábil que ele não tinha como conferir na tela.
 *
 * `search` fica DE FORA de propósito: filtro de texto livre serve para
 * procurar, não para delimitar ação em massa.
 */
export interface IRecorteConciliar {
  ano?: number;
  mes?: number;
  banco?: string;
  conta?: string;
}

const paramsRecorte = (r?: IRecorteConciliar): Record<string, string | number> => {
  const p: Record<string, string | number> = {};
  if (r?.ano) p.ano = r.ano;
  if (r?.mes) p.mes = r.mes;
  if (r?.banco) p.banco = r.banco;
  if (r?.conta) p.conta = r.conta;
  return p;
};

/** IA concilia lancamentos pendentes (auto). 1 fatia por chamada — o modal
 *  de auto-conciliar chama em loop ate zerar (limite clampado a 180 no back). */
export const useContaCilIaConciliar = () => {
  const invalidar = useInvalidarIa();
  return useMutation({
    mutationFn: async (params: {
      empresaId: number;
      limite?: number;
      recorte?: IRecorteConciliar;
    }): Promise<IIaResultado> => {
      const res = await api.post<IApiEnvelope<IIaResultado>>(`${BASE}/ia/conciliar`, null, {
        params: {
          empresa_id: params.empresaId,
          limite: params.limite ?? 120,
          ...paramsRecorte(params.recorte),
        },
      });
      return res.data.data;
    },
    onSuccess: invalidar,
  });
};

export interface IConciliarAutoResult {
  conciliados: number;
  sugeridos: number;
  conflitos: number;
  transferencias?: number;
  analisados: number;
  deduplicados?: number;
}

/** Motor deterministico GRATIS (P0 aplicacao + P2 memoria + transferencia +
 *  socio/cliente). Roda antes da IA no modal — pega o resolvivel de graca. */
export const useContaCilConciliarAuto = () => {
  const invalidar = useInvalidarIa();
  return useMutation({
    mutationFn: async (params: {
      empresaId: number;
      recorte?: IRecorteConciliar;
    }): Promise<IConciliarAutoResult> => {
      const res = await api.post<IApiEnvelope<IConciliarAutoResult>>(`${BASE}/conciliar`, null, {
        params: { empresa_id: params.empresaId, ...paramsRecorte(params.recorte) },
      });
      return res.data.data;
    },
    onSuccess: invalidar,
  });
};

/** Preview do auto-conciliar: contagens + base de custo (pro modal). */
export const useContaCilIaConciliarPreview = (
  empresaId?: number | null,
  enabled = true,
  // Com default (e não opcional) por causa do `default-param-last`: um
  // parâmetro opcional depois de um com default viola a regra do lint.
  recorte: IRecorteConciliar = {},
) =>
  useQuery({
    // O recorte entra na queryKey: sem ele, trocar de mês devolveria a contagem
    // cacheada do mês anterior e o modal prometeria um número que não é o que
    // vai ser processado.
    queryKey: ['contacil', 'regras', 'ia-conciliar-preview', empresaId ?? null, recorte] as const,
    enabled: enabled && empresaId != null,
    staleTime: 15 * 1000,
    queryFn: async (): Promise<IIaConciliarPreview> => {
      const res = await api.get<IApiEnvelope<IIaConciliarPreview>>(`${BASE}/ia/conciliar/preview`, {
        params: { empresa_id: empresaId, ...paramsRecorte(recorte) },
      });
      return res.data.data;
    },
  });

/** Historico de execucoes da IA. */
export const useContaCilIaLog = (empresaId?: number | null) =>
  useQuery({
    queryKey: ['contacil', 'regras', 'ia-log', empresaId ?? null] as const,
    enabled: empresaId != null,
    queryFn: async (): Promise<IIaExecucao[]> => {
      const res = await api.get<IApiEnvelope<IIaExecucao[]>>(`${BASE}/ia/log`, {
        params: { empresa_id: empresaId },
      });
      return res.data.data;
    },
  });

/** Reverte uma execucao da IA. */
export const useContaCilIaReverter = () => {
  const invalidar = useInvalidarIa();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      params: number | { execucaoId: number; motivo?: string },
    ): Promise<{ regras_removidas: number; lancamentos_revertidos: number }> => {
      // Backcompat: aceita number (legado) OU objeto com motivo (Sprint 2).
      const execucaoId = typeof params === 'number' ? params : params.execucaoId;
      const motivo = typeof params === 'number' ? undefined : params.motivo;
      const res = await api.post<
        IApiEnvelope<{ regras_removidas: number; lancamentos_revertidos: number }>
      >(`${BASE}/ia/reverter/${execucaoId}`, { motivo });
      return res.data.data;
    },
    onSuccess: () => {
      invalidar();
      qc.invalidateQueries({ queryKey: ['contacil', 'regras', 'ia-log'] });
    },
  });
};
